import { atoms, useResetAtoms } from "#/lib/atoms/atoms";
import cookies from "#/lib/cookies";
import { queryClient } from "#/lib/query";
import queryKeys from "#/lib/query-keys";
import { fetchRequest } from "#/lib/stream-api";
import { useSetAtom } from "jotai";
import { useRouter } from "next/navigation";
import { useCallback } from "react";

export const useOnLogin = () => {
  const router = useRouter();
  const resetAtoms = useResetAtoms();
  const setMid = useSetAtom(atoms.mid);
  const setAccessToken = useSetAtom(atoms.accessToken);
  const setOrgId = useSetAtom(atoms.orgId);
  const setCurrentMenu = useSetAtom(atoms.currentMenuId);
  const setCurrentLocation = useSetAtom(atoms.currentLocationId);
  const setCategoryActionsPopoverOpen = useSetAtom(
    atoms.categoryActionsPopoverOpen,
  );

  const setCurrentOrgMerchant = useCallback(
    (options: { org_id: string; merchant_id: string }) => {
      const { org_id, merchant_id } = options;
      cookies.set(cookies.options.org_id, org_id);
      cookies.set(cookies.options.merchant_id, merchant_id);
      // Update cookies + atoms *urgently* (no startTransition) so the identity
      // suspense queries — useUser (keyed on orgId) and useMerchantQuery (keyed
      // on mid) — re-key and suspend the top-level ProtectedPage boundary,
      // showing the full-page loader until the new org's identity is fetched.
      // A transition here would tell React to keep the previous org's UI on
      // screen and let data pop in piecemeal instead. We deliberately do NOT
      // clear the whole cache (that lives in onLogin, the real session
      // boundary): the identity queries gate the full loader, and the shell's
      // locations/integrations queries re-key under their own boundaries so
      // they show in-page skeletons once identity resolves and the shell
      // remounts. Locations use merchant_id only in headers, so refetch them
      // explicitly for any consumer that stays mounted.
      setCurrentMenu(null);
      setCurrentLocation(null);
      setCategoryActionsPopoverOpen({});
      setOrgId(org_id);
      setMid(merchant_id);
      queryClient.refetchQueries({
        queryKey: queryKeys.locations(merchant_id),
        type: "active",
      });
    },
    [
      setMid,
      setOrgId,
      setCurrentMenu,
      setCurrentLocation,
      setCategoryActionsPopoverOpen,
    ],
  );

  const onLogin = useCallback(
    async (
      access_token,
      refresh_token,
      skip_redirect?: boolean,
      location_id?: string | null,
    ) => {
      resetAtoms();
      // Login is the one genuine session boundary: fully wipe the cache so no
      // previous user's data can leak through on re-login without a reload.
      // (setCurrentOrgMerchant no longer clears — switches are optimistic.)
      queryClient.clear();
      cookies.delete(cookies.options.org_id);
      cookies.delete(cookies.options.merchant_id);
      cookies.set(cookies.options.access_token, access_token);
      setAccessToken(access_token);
      cookies.set(cookies.options.refresh_token, refresh_token);
      const orgs = await queryClient.fetchQuery({
        queryKey: queryKeys.orgs(),
        staleTime: 0,
        queryFn: () => fetchRequest("/org", { headers: { access_token } }),
      });
      const org_id = orgs.find((o) => o?.is_default)?._id || orgs[0]?._id;
      const merchant_id = orgs.find((o) => o?._id === org_id)?.merchants[0]
        ?._id;
      setCurrentOrgMerchant({
        org_id,
        merchant_id,
      });
      if (location_id) {
        setCurrentLocation(location_id);
      }
      if (!skip_redirect) {
        await router.push("/menu");
        return {};
      } else {
        return {
          merchant_id: orgs[0]?.merchants[0]?._id,
        };
      }
    },
    [router, setAccessToken, setCurrentOrgMerchant, setCurrentLocation, resetAtoms],
  );

  return {
    onLogin,
    setCurrentOrgMerchant,
  };
};
